import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "../../../node_modules/tabulator-tables/dist/css/tabulator_bootstrap4.css";
import "../../../node_modules/semantic-ui-css/components/icon.min.css";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import React from "react";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

type InfoRowProps = {
  icon?: string;
  label: React.ReactNode;
  children: React.ReactNode;
  inlineIcon?: boolean;
};

const InfoRow = ({ icon, label, children, inlineIcon = false }: InfoRowProps) => (
  <div className="alert-escalar-detail__row">
    <div className="alert-escalar-detail__label">
      {inlineIcon ? (
        <span className="alert-escalar-detail__label-inline">{label}</span>
      ) : (
        <>
          {icon && <i className={`${icon} icon alert-escalar-detail__icon`}></i>}
          <span className="alert-escalar-detail__label-text">{label}</span>
        </>
      )}
    </div>
    <div className="alert-escalar-detail__value">{children}</div>
  </div>
);

const MenuEscalarAlertasGateway: React.FC = () => {
  const query = useQuery();
  const idPlaceAlert = query.get("idPlaceAlert");
  const tableRef = useRef<HTMLDivElement>(null);
  const [alertData, setAlertData] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<any>(null);

  const formatTimeLabel = (mins: number) => (
    <span className="alert-escalar-time-label">
      <strong>{mins}</strong>
      <span className="alert-escalar-time-unit">min</span>
    </span>
  );

  useEffect(() => {
    const fetchAlertData = async () => {
      if (!idPlaceAlert) return;
      try {
        const response = await fetch(`${backendUrl}/api/PlaceAlerts/${idPlaceAlert}`);
        const dataResponse = await response.json();
        setAlertData(dataResponse);
      } catch (error) {
        console.error("Erro ao buscar dados dos alertas:", error);
      }
    };

    fetchAlertData();
  }, [idPlaceAlert, backendUrl]);

  const fetchEscalationData = async () => {
    if (!idPlaceAlert) return;
    try {
      const response = await fetch(
        `${backendUrl}/api/PlaceAlertsEscalate/ByPlaceAlert/${idPlaceAlert}`
      );
      const fetchedData = await response.json();

      const levelsNeeded = [2, 3];
      levelsNeeded.forEach((level) => {
        if (!fetchedData.some((item: { Level: number }) => item.Level === level)) {
          fetchedData.push({ Level: level, isNew: true });
        }
      });

      fetchedData.sort((a: any, b: any) => a.Level - b.Level);
      setData(fetchedData);
    } catch (error) {
      console.error("Erro ao buscar dados de escalonamento:", error);
      setData([{ Level: 2, isNew: true }, { Level: 3, isNew: true }]);
    }
  };

  useEffect(() => {
    fetchEscalationData();
  }, [idPlaceAlert, backendUrl]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/User`);
        const result = await response.json();
        setUsers(result);
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
      }
    };

    fetchUsers();
  }, [backendUrl]);

  const saveOrUpdateData = async (rowData: any) => {
    const isNewRecord = rowData.isNew === true || !rowData.Id;
    const method = isNewRecord ? "POST" : "PUT";

    const url = isNewRecord
      ? `${backendUrl}/api/PlaceAlertsEscalate`
      : `${backendUrl}/api/PlaceAlertsEscalate/${rowData.Id}`;

    let payload;

    if (isNewRecord) {
      payload = {
        IdPlaceAlerts: Number(idPlaceAlert),
        Level: rowData.Level,
        TimeToResolveMinutes: rowData.TimeToResolveMinutes,
        TimeForResponseMinutes: rowData.TimeForResponseMinutes,
        EmailResponsible: rowData.EmailResponsible || null,
      };
    } else {
      payload = {
        Id: rowData.Id,
        IdPlaceAlerts: rowData.IdPlaceAlerts,
        Level: rowData.Level,
        TimeToResolveMinutes: rowData.TimeToResolveMinutes,
        TimeForResponseMinutes: rowData.TimeForResponseMinutes,
        EmailResponsible: rowData.EmailResponsible || null,
        DtCriacao: rowData.DtCriacao,
        DtAlteracao: rowData.DtAlteracao,
      };
    }

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        fetchEscalationData();
        setIsModalOpen(false);
      } else {
        const errorText = await response.text();
        console.error("Erro na requisição:", response.status, errorText);
        alert(`Erro ao salvar: ${errorText}`);
      }
    } catch (error) {
      console.error("Erro de conexão ao salvar:", error);
      alert("Erro de conexão ao tentar salvar.");
    }
  };

  const openEditModal = (rowData: any) => {
    setEditingRow({ ...rowData });
    setIsModalOpen(true);
  };

  const handleModalSave = () => {
    if (editingRow) {
      const finalData = {
        ...editingRow,
        IdPlaceAlerts: Number(idPlaceAlert),
        TimeForResponseMinutes: Number(editingRow.TimeForResponseMinutes) || 0,
        TimeToResolveMinutes: Number(editingRow.TimeToResolveMinutes) || 0,
      };
      saveOrUpdateData(finalData);
    }
  };

  useEffect(() => {
    if (tableRef.current && data.length > 0) {
      const table = new Tabulator(tableRef.current, {
        data,
        layout: "fitDataTable",
        placeholder: "Sem dados de escalonamento",
        rowHeight: 50,
        resizableColumnFit: false,
        headerSort: false,
        columns: [
          {
            title: "NÍVEL",
            field: "Level",
            hozAlign: "center",
            width: 100,
            resizable: false,
            formatter: (cell) => {
              const val = cell.getValue();
              const levelClass =
                val === 2
                  ? "alert-escalar-table-badge--level-2"
                  : "alert-escalar-table-badge--level-3";
              return `<span class="alert-escalar-table-badge ${levelClass}">${val}º</span>`;
            },
          },
          {
            title: "T. RESPOSTA",
            field: "TimeForResponseMinutes",
            hozAlign: "center",
            resizable: false,
            width: 200,
            formatter: (cell) =>
              cell.getValue()
                ? `<span class="alert-escalar-table-value">${cell.getValue()} min</span>`
                : "-",
          },
          {
            title: "T. SOLUÇÃO",
            field: "TimeToResolveMinutes",
            hozAlign: "center",
            resizable: false,
            width: 200,
            formatter: (cell) =>
              cell.getValue()
                ? `<span class="alert-escalar-table-value">${cell.getValue()} min</span>`
                : "-",
          },
          {
            title: "RESPONSÁVEL (EMAIL)",
            field: "EmailResponsible",
            resizable: false,
            minWidth: 490,
            formatter: (cell) =>
              cell.getValue()
                ? `<span class="alert-escalar-table-email"><i class="user outline icon"></i>${cell.getValue()}</span>`
                : "-",
          },
          {
            title: "AÇÕES",
            hozAlign: "center",
            headerSort: false,
            resizable: false,
            width: 140,
            formatter: function (cell) {
              if (cell.getRow().getData().Level >= 2) {
                return `<button class='alert-escalar-action-btn edit-btn' title='Editar'><i class='pencil alternate icon'></i></button>`;
              }
              return "";
            },
            cellClick: function (e, cell) {
              const rowData = cell.getRow().getData();
              const target = e.target as HTMLElement;
              if (target.closest(".edit-btn")) {
                openEditModal(rowData);
              }
            },
          },
        ],
      });

      return () => {
        table.destroy();
      };
    }
  }, [data, users, idPlaceAlert]);

  return (
    <div className="alert-escalar-page">
      <div className="alert-escalar-card alert-escalar-card--left">
        <div className="alert-escalar-card__header">
          <i className="microchip icon alert-escalar-icon alert-escalar-icon--info"></i>
          Dados do Alerta Selecionado
        </div>

        {alertData ? (
          <div className="alert-escalar-detail">
            <InfoRow icon="layer group" label="Nível">
              <span className="alert-escalar-badge">
                {alertData.Level ?? alertData.IdAlertLevels}
              </span>
            </InfoRow>
            <InfoRow
              inlineIcon
              label={
                <>
                  <i className="map marker alternate icon alert-escalar-detail__icon"></i>
                  <span className="alert-escalar-detail__label-text-inline">Local</span>
                </>
              }
            >
              {alertData.Place ?? alertData.IdPlace}
            </InfoRow>
            <InfoRow icon="microchip" label="Gateway ID">
              <strong>{alertData.IdGateway}</strong>
            </InfoRow>
            <InfoRow icon="stopwatch" label="Delay Início">
              {formatTimeLabel(alertData.DelayToStart_minutes)}
            </InfoRow>
            <InfoRow icon="hourglass half" label="Tempo Solução">
              {formatTimeLabel(alertData.TimeToResolve_minutes)}
            </InfoRow>
            <InfoRow icon="user circle" label="Resp. Inicial">
              {alertData.IdUserResponsible}
            </InfoRow>
          </div>
        ) : (
          <div className="alert-escalar-loading">
            <div className="ui active inline loader"></div>
          </div>
        )}
      </div>

      <div className="alert-escalar-card alert-escalar-card--right">
        <div className="alert-escalar-card__header">
          <i className="sort amount up icon alert-escalar-icon alert-escalar-icon--warning"></i>
          Fluxo de Escalonamento
        </div>
        <div className="alert-escalar-card__body">
          <div ref={tableRef} className="alert-escalar-table"></div>
        </div>
      </div>

      {isModalOpen && editingRow && (
        <div className="alert-escalar-modal-overlay">
          <div className="alert-escalar-modal">
            <div className="alert-escalar-modal__header">
              <h3>Editar Nível {editingRow.Level}</h3>
              <button
                type="button"
                className="alert-escalar-modal__close"
                onClick={() => setIsModalOpen(false)}
              >
                <i className="close icon"></i>
              </button>
            </div>

            <div className="alert-escalar-form-group">
              <label className="alert-escalar-form-label">Tempo para Resposta (minutos)</label>
              <input
                type="number"
                className="alert-escalar-input"
                value={editingRow.TimeForResponseMinutes}
                onChange={(e) =>
                  setEditingRow({ ...editingRow, TimeForResponseMinutes: e.target.value })
                }
              />
            </div>

            <div className="alert-escalar-form-group">
              <label className="alert-escalar-form-label">Tempo para Solução (minutos)</label>
              <input
                type="number"
                className="alert-escalar-input"
                value={editingRow.TimeToResolveMinutes}
                onChange={(e) =>
                  setEditingRow({ ...editingRow, TimeToResolveMinutes: e.target.value })
                }
              />
            </div>

            <div className="alert-escalar-form-group">
              <label className="alert-escalar-form-label">Responsável</label>
              <select
                className="alert-escalar-input alert-escalar-select"
                value={editingRow.EmailResponsible || ""}
                onChange={(e) => setEditingRow({ ...editingRow, EmailResponsible: e.target.value })}
              >
                <option value="">Selecione um usuário...</option>
                {users.map((user, index) => (
                  <option key={index} value={user.Email}>
                    {user.Email}
                  </option>
                ))}
              </select>
            </div>

            <div className="alert-escalar-modal__actions">
              <button
                type="button"
                className="alert-escalar-btn alert-escalar-btn--ghost"
                onClick={() => setIsModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="alert-escalar-btn alert-escalar-btn--primary"
                onClick={handleModalSave}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuEscalarAlertasGateway;